/**
 * Where an image a reply cites comes from: a remote `https:` image the browser loads as it is, a
 * file of the conversation's workspace (`avatars/trump/photo.jpg`, `./shot.png`, or an absolute
 * path inside the workspace) read through its file space, or nothing the transcript may show (a
 * path that leaves the workspace, another scheme).
 */
export type ImageSource =
  | { kind: 'remote'; url: string }
  | { kind: 'workspace'; path: string }
  | { kind: 'unavailable' };

const SCHEME = /^[a-z][a-z0-9+.-]*:/i;

/** `src` as the reply wrote it; `root` is the workspace's absolute path when it is known. */
export function imageSource(src: string | undefined, root?: string | null): ImageSource {
  const value = src?.trim() ?? '';
  if (value === '') return { kind: 'unavailable' };
  if (/^https?:\/\//i.test(value)) return { kind: 'remote', url: value };
  let path = value;
  if (/^file:\/\//i.test(path)) path = path.slice('file://'.length);
  else if (SCHEME.test(path) && !/^[a-z]:[\\/]/i.test(path)) return { kind: 'unavailable' };
  try {
    path = decodeURIComponent(path.split(/[?#]/, 1)[0] as string);
  } catch {
    return { kind: 'unavailable' };
  }
  path = path.replaceAll('\\', '/');
  if (path.startsWith('/') || /^[a-z]:\//i.test(path)) {
    const base = root?.replaceAll('\\', '/').replace(/\/+$/, '');
    if (!base || !path.toLowerCase().startsWith(`${base.toLowerCase()}/`))
      return { kind: 'unavailable' };
    path = path.slice(base.length + 1);
  }
  const segments = path.split('/').filter((segment) => segment !== '' && segment !== '.');
  if (
    segments.length === 0 ||
    segments.some((segment) => segment === '..' || segment.includes('\0'))
  )
    return { kind: 'unavailable' };
  return { kind: 'workspace', path: segments.join('/') };
}

/** Image types the transcript shows from a workspace (the browser renders them as they are). */
const IMAGE_TYPES: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  avif: 'image/avif',
  svg: 'image/svg+xml',
};

/** The media type of a workspace image by its extension, or null for another file. */
export function workspaceImageType(path: string): string | null {
  const extension = path.slice(path.lastIndexOf('.') + 1).toLowerCase();
  return IMAGE_TYPES[extension] ?? null;
}
